/**
 * Tailwind/CSS implementation of the PeekIn component.
 * Identical composition to PeekInStyled, but the root layout wrappers PeekIn
 * authors directly (previously styled-system props on Box) are expressed as
 * Seeds PeekIn CSS classes from peek-in.css. The default rendering path.
 */

import * as React from "react";
import Button from "@sproutsocial/seeds-react-button";
import Drawer from "@sproutsocial/seeds-react-drawer";
import Icon from "@sproutsocial/seeds-react-icon";
// eslint-disable-next-line import/no-deprecated
import Text from "@sproutsocial/seeds-react-text";
import { useIsMobile } from "@sproutsocial/seeds-react-hooks";
import { Modal, ModalBody } from "@sproutsocial/seeds-react-modal/v2";
import { PeekInContext } from "./PeekInContext";
import {
  PeekInHeader,
  PeekInContent,
  PeekInFooter,
  PeekInPanel,
  PeekInTrigger,
} from "./components";
import { PEEK_IN_WIDTH, PEEK_IN_HEIGHT } from "./constants";
import { usePeekInPanelState } from "./usePeekInPanelState";
import type { TypePeekInProps } from "./PeekInTypes";
import type { TypeModalActionProps } from "@sproutsocial/seeds-react-modal/v2";
import type { TypeDrawerActionProps } from "@sproutsocial/seeds-react-drawer";

// Helper to extract a child element matching a given component type
function getChildByType<T extends React.FC<any>>(
  children: React.ReactNode,
  type: T
): React.ReactElement | null {
  const childArray = React.Children.toArray(children);
  return (
    (childArray.find(
      (child) => React.isValidElement(child) && child.type === type
    ) as React.ReactElement) || null
  );
}

export const PeekInTailwind = ({
  open,
  defaultOpen,
  onOpenChange,
  "aria-label": ariaLabelProp,
  closeButtonAriaLabel = "Close",
  actions,
  panelToggle,
  snapPoints,
  defaultSnapPoint,
  snapPoint,
  onSnapPointChange,
  snapToSequentialPoints,
  disableCloseOnClickOutside,
  actionsInHeader,
  children,
}: TypePeekInProps) => {
  // Extract child slots by component type
  const header = getChildByType(children, PeekInHeader);
  const content = getChildByType(children, PeekInContent);
  const footer = getChildByType(children, PeekInFooter);
  const panel = getChildByType(children, PeekInPanel);
  const trigger = getChildByType(children, PeekInTrigger);

  // Panel open/closed state — controlled by the panel's own open/onOpenChange
  // when supplied, otherwise internal and defaulting to true (open)
  const { panelOpen, setPanelOpen } = usePeekInPanelState(panel);
  const panelId = React.useId();

  // PeekIn owns its open state on both renderings. The Drawer is always
  // controlled, and driving Modal in controlled mode too means a close asked
  // for from inside — a footer button, the panel — behaves identically on
  // desktop and mobile, and leaves PeekIn independent of whichever dialog
  // primitive Modal happens to compose.
  const isControlled = open !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen ?? false);
  const effectiveOpen = isControlled ? open ?? false : internalOpen;

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange]
  );

  const handleClose = React.useCallback(
    () => handleOpenChange(false),
    [handleOpenChange]
  );

  const contextValue = React.useMemo(
    () => ({ onClose: handleClose, panelOpen, setPanelOpen, panelId }),
    [handleClose, panelOpen, setPanelOpen, panelId]
  );

  // Accessible name: an explicit aria-label wins, then the header title or
  // subtitle when either is a plain string. A ReactNode title cannot be read as
  // text, so those call sites have no usable name without aria-label.
  const headerTitle = header?.props?.title;
  const headerSubtitle = header?.props?.subtitle;
  const ariaLabel =
    ariaLabelProp ??
    (typeof headerTitle === "string" ? headerTitle : undefined) ??
    (typeof headerSubtitle === "string" ? headerSubtitle : undefined);

  React.useEffect(() => {
    if (process.env.NODE_ENV !== "production" && !ariaLabel) {
      // eslint-disable-next-line no-console
      console.warn(
        "PeekIn: no accessible name was resolved. Pass `aria-label`, or give " +
          "PeekInHeader a string `title`."
      );
    }
  }, [ariaLabel]);

  // Derive the trigger element for Modal's modalTrigger prop.
  const triggerElement = trigger
    ? (trigger.props.children as React.ReactElement)
    : undefined;

  // Build the actions array for ModalV2's action bar (desktop)
  const modalActions = React.useMemo(() => {
    const result: TypeModalActionProps[] = [];

    // Panel toggle action
    if (panelToggle && panel) {
      result.push({
        "aria-label": panelToggle["aria-label"] ?? "Toggle panel",
        iconName: panelToggle.iconName,
        onClick: () => setPanelOpen((prev: boolean) => !prev),
        "aria-expanded": panelOpen,
        "aria-controls": panelId,
      } as TypeModalActionProps);
    }

    // Consumer-provided actions
    if (actions) {
      result.push(...actions);
    }

    return result.length > 0 ? result : undefined;
  }, [panelToggle, panel, actions, panelOpen, panelId]);

  // Build the actions array for the mobile Drawer's action rail. Same shape
  // as modalActions but without the close button — the Drawer renders that
  // itself based on `closeButtonLabel`/`onClose`.
  const drawerActions = React.useMemo(() => {
    const result: TypeDrawerActionProps[] = [];

    if (panelToggle && panel) {
      result.push({
        "aria-label": panelToggle["aria-label"] ?? "Toggle panel",
        iconName: panelToggle.iconName,
        onClick: () => setPanelOpen((prev: boolean) => !prev),
        "aria-expanded": panelOpen,
        "aria-controls": panelId,
      } as TypeDrawerActionProps);
    }

    if (actions) {
      result.push(...actions);
    }

    return result.length > 0 ? result : undefined;
  }, [panelToggle, panel, actions, panelOpen, panelId]);

  // ── Mobile: PeekIn becomes a bottom-sheet Drawer ──────────────────────────
  const isMobile = useIsMobile();

  // On mobile, clone the trigger's child and attach a direct onClick — there is
  // no dialog trigger context outside the desktop Modal.
  const mobileTrigger = trigger
    ? React.cloneElement(trigger.props.children as React.ReactElement, {
        onClick: () => handleOpenChange(true),
      })
    : undefined;

  if (isMobile) {
    return (
      <PeekInContext.Provider value={contextValue}>
        {mobileTrigger}
        <Drawer
          open={effectiveOpen}
          onClose={handleClose}
          direction="bottom"
          closeButtonLabel={closeButtonAriaLabel}
          snapPoints={snapPoints}
          defaultSnapPoint={defaultSnapPoint}
          snapPoint={snapPoint}
          onSnapPointChange={onSnapPointChange}
          snapToSequentialPoints={snapToSequentialPoints}
          disableCloseOnClickOutside={disableCloseOnClickOutside}
          actions={drawerActions}
          actionsInHeader={actionsInHeader}
          aria-label={ariaLabel}
        >
          <Drawer.Header
            render={(ctx) => (
              <div className="seeds-peek-in-drawer-header">
                {/* Left: optional back button + title */}
                <div className="seeds-peek-in-titlegroup">
                  {header?.props?.onBack && (
                    <Button
                      appearance="unstyled"
                      onClick={header.props.onBack}
                      aria-label="Back"
                      data-qa-peek-in-back
                    >
                      <Icon name="arrow-left-outline" size="small" />
                    </Button>
                  )}
                  <div className="seeds-peek-in-titlestack">
                    <Text
                      as="h2"
                      fontSize={400}
                      fontWeight="semibold"
                      color="text.headline"
                      data-qa-peek-in-title
                    >
                      {header?.props?.title}
                    </Text>
                    {header?.props?.subtitle && (
                      <Text
                        as="div"
                        fontSize={200}
                        color="text.body"
                        data-qa-peek-in-subtitle
                      >
                        {header.props.subtitle}
                      </Text>
                    )}
                  </div>
                </div>
                {/* Right: consumer header actions, plus pills when the rail
                    is disabled (actionsInHeader). The rail owns the close
                    button when it renders; otherwise we render it inline so
                    every mobile sheet has a visible close affordance. */}
                <div className="seeds-peek-in-header-actions">
                  {header?.props?.children}
                  {actionsInHeader && panelToggle && panel && (
                    <Button
                      appearance="pill"
                      aria-label={panelToggle["aria-label"] ?? "Toggle panel"}
                      onClick={() => setPanelOpen((p: boolean) => !p)}
                    >
                      <Icon aria-hidden name={panelToggle.iconName} />
                    </Button>
                  )}
                  {actionsInHeader &&
                    actions?.map((action, i) => (
                      <Button
                        key={i}
                        appearance="pill"
                        aria-label={action["aria-label"]}
                        onClick={action.onClick}
                        disabled={action.disabled}
                      >
                        {action.iconName && (
                          <Icon aria-hidden name={action.iconName} />
                        )}
                      </Button>
                    ))}
                  {(actionsInHeader || !drawerActions) && (
                    <Button
                      appearance="pill"
                      aria-label={ctx.closeButtonLabel}
                      onClick={ctx.onClose}
                    >
                      <Icon aria-hidden name="x-outline" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          />
          {content}
          {footer}
          {/* PeekInPanel renders as a nested Drawer on mobile */}
          {panel}
        </Drawer>
      </PeekInContext.Provider>
    );
  }

  // ── Desktop: full-screen Modal ────────────────────────────────────────────
  return (
    <PeekInContext.Provider value={contextValue}>
      <Modal
        open={effectiveOpen}
        onOpenChange={handleOpenChange}
        width={PEEK_IN_WIDTH}
        height={PEEK_IN_HEIGHT}
        closeButtonAriaLabel={closeButtonAriaLabel}
        aria-label={ariaLabel}
        modalTrigger={triggerElement}
        actions={modalActions}
        bg="transparent"
      >
        {/* Layout: [Left column] [Rail] */}
        <ModalBody padding={0} display="flex" overflow="hidden">
          {/* Left column: header + main content */}
          <div className="seeds-peek-in-main">
            {header}
            {content}
            {footer}
          </div>
          {/* Right column: PeekInPanel (separate panel alongside main) */}
          {panel}
        </ModalBody>
      </Modal>
    </PeekInContext.Provider>
  );
};

PeekInTailwind.displayName = "PeekIn";
